/**
 * The pure rules behind the Replace wizard — kept out of the component so they
 * can be tested without rendering it.
 */
import type { PendingReplacementRow, ReplacementReason } from '../../services/hardwareApi';

export type ReplaceStep =
  | 'why' | 'links' | 'unlock' | 'detach' | 'restore' | 'pick' | 'handover' | 'install' | 'attach'
  | 'reader' | 'tender' | 'done';

/**
 * HAS THE REPLACEMENT HAD RUBYTILL INSTALLED FOR THIS SWAP?
 *
 * `osInstall` is server-owned and stamped only by a verified install. It counts
 * when it is:
 *   - present,
 *   - newer than any `reinstall.requestedAt`. A reinstall request that is newer
 *     than the install means the till was sent back to be built again and has
 *     not been rebuilt yet (see the note on `DeviceUnitRow.reinstall`),
 *   - at or after `sinceMs`, when this replacement started. An install from
 *     before the swap belongs to the device's earlier life, not to this
 *     register.
 */
export function osInstalledForReplacement(
  unit: { osInstall?: { installedAt?: number } | null; reinstall?: { requestedAt?: number } | null } | null | undefined,
  sinceMs: number,
): boolean {
  const at = unit?.osInstall?.installedAt;
  if (!at) return false;
  const requested = unit?.reinstall?.requestedAt;
  if (requested && !(at > requested)) return false;
  return at >= (sinceMs || 0);
}

export type ReplaceOutcome = PendingReplacementRow['outcome'];

/** Still with us — the bench-swap road (release, reset, allocate another). */
export const PRE_DISPATCH = new Set([
  'allocated', 'awaiting', 'processing', 'on_hold', 'backordered', 'provisioning', 'configured', 'packing',
]);

/** Left us — the field-replacement road (`POST /lifecycle/units/{id}/replace`). */
export const POST_DISPATCH = new Set(['shipped', 'delivered', 'deployed']);

/**
 * States a device does not come back from on this order. A partner in one of
 * these is still ON the order (it keeps its orderId as part of the order's
 * story) but must never be paired with a replacement — a real case paired a
 * fresh till with a reader that had been marked lost.
 */
export const DEAD_STATES = new Set(['lost', 'retired', 'rma', 'returned']);

/**
 * May a field replacement be offered for this unit? Shipped/delivered/deployed,
 * or already recorded as returned/lost/rma/retired AFTER shipping (the server
 * reads the shipment stamp, not only the state), and not replaced already.
 */
export const canReplaceShipped = (u: {
  state?: string; orderId?: string; replacedByUnitId?: string; shipment?: { shippedAt?: number } | null;
}): boolean =>
  !!u.orderId && !u.replacedByUnitId
  && (POST_DISPATCH.has(String(u.state)) || (DEAD_STATES.has(String(u.state)) && !!u.shipment?.shippedAt));

/**
 * IS THIS PARTNER ONE THE REPLACEMENT SHOULD BE PAIRED WITH?
 * On THIS order and in a live state. Returns the sentence to show when not.
 */
export function livePartnerProblem(
  partner: { state?: string; orderId?: string; trackingId?: string; serialNumber?: string; unitId: string } | null | undefined,
  orderId: string | undefined,
): string | undefined {
  if (!partner) return 'Nothing to pair with.';
  const name = partner.trackingId || partner.serialNumber || partner.unitId;
  if (!orderId || partner.orderId !== orderId) return `${name} is no longer on this order — not paired.`;
  if (DEAD_STATES.has(String(partner.state))) return `${name} is ${partner.state} — not paired.`;
  return undefined;
}

/**
 * The shipped-device reasons map one-to-one onto the wizard's outcomes, so a
 * pending replacement needs no extra field to know which reason to send when
 * the hand-over resumes.
 */
export const OUTCOME_FOR_REASON: Record<ReplacementReason, ReplaceOutcome> = {
  returned: 'good',
  lost_in_transit: 'lost',
  warranty: 'rma',
  damaged: 'destroy',
};
export const REASON_FOR_OUTCOME: Record<ReplaceOutcome, ReplacementReason> = {
  good: 'returned',
  lost: 'lost_in_transit',
  rma: 'warranty',
  destroy: 'damaged',
};

/** Card tenders that are driven through a registered reader (need an emvDeviceId). */
export const DEVICE_BACKED_TENDERS = ['credit_dejavoo', 'credit_dejavoo_cloud', 'credit_dejavoo_rtpay', 'credit_nmi_cloud', 'credit_nmi_direct'];

/** One credit type per register — the list the service's mutex enforces. */
export const CREDIT_TYPES = ['credit_external', 'credit_nmi_cloud', 'credit_nmi_direct', 'credit_dejavoo', 'credit_dejavoo_cloud', 'credit_dejavoo_rtpay'];

/** The register's tenders with `cardTender` switched on, replacing any other credit type. */
export const withCardTender = (current: readonly string[], cardTender: string): string[] => [
  ...current.filter((x) => x !== cardTender && !CREDIT_TYPES.includes(x) && x !== 'external_card'),
  cardTender,
];

/** The device-backed card tender a register currently has on, if any. */
export const cardTenderOf = (enabled: readonly string[] | null | undefined): string | undefined =>
  (enabled || []).find((t) => DEVICE_BACKED_TENDERS.includes(t));

/**
 * Device management's install screen for one unit. The same URL
 * PendingConfigurationTab navigates to; the wizard and the order banner open
 * it in a NEW tab so the order (and a wizard in progress) stay where they are.
 */
export const installUrlFor = (unitId: string): string =>
  `/pos-config?tab=install&unitId=${encodeURIComponent(unitId)}`;

/**
 * WHICH DEVICE IS REPLACING THIS ONE — read from live data, the same way for
 * the wizard's resume and the order banner. The shipped road names it on the
 * old unit (`replacedByUnitId`); a bench swap names it in the old unit's
 * history, on the `replaced_by` line written before the allocation, since the
 * swap started.
 */
export function replacementIdFor(
  pending: { postDispatch?: boolean; startedAt?: number },
  old: { replacedByUnitId?: string } | null | undefined,
  timeline: { kind: string; at: number; meta?: Record<string, any> }[],
): string | undefined {
  if (pending.postDispatch) return old?.replacedByUnitId || undefined;
  return timeline
    .filter((e) => e.at >= (pending.startedAt || 0))
    .find((e) => e.kind === 'replaced_by' && e.meta?.replacedByUnitId)?.meta?.replacedByUnitId;
}

export interface ResumeInput {
  pending: Pick<PendingReplacementRow, 'outcome' | 'postDispatch' | 'lane'>;
  /** The OUTGOING unit as it is now. */
  old: { state: string; orderId?: string; replacedByUnitId?: string };
  orderId: string;
  /**
   * A replacement already allocated to this order for this swap (found from
   * live data). On the shipped road, the replacement the old unit names as
   * `replacedByUnitId` counts too.
   */
  replacementOnOrder: boolean;
  /** POS lane: the replacement has had its OS installed since this swap started (`osInstalledForReplacement`). */
  osInstalled: boolean;
  /** Every attach task is already true of live data (register, pairing, sheet). */
  attachComplete: boolean;
  /** Payment lane: the replacement reader is in the location's pool by serial. */
  readerInPool: boolean;
  /** Pre-dispatch "good": the reset checklist has been recorded since the swap started. */
  restoreRecorded: boolean;
}

/**
 * WHERE A RESUMED REPLACEMENT PICKS UP — decided from the old unit's CURRENT
 * state and live data, never from anything the closed wizard held in memory.
 *
 *   replacement already on the order (or, shipped road, named as replacedBy):
 *     POS lane, OS not installed     → Install the OS
 *     attach tasks not all done      → Attach it
 *     card reader                    → register it / its tender
 *     everything done                → Attach it (every task ticks "already",
 *                                       then Finish)
 *   shipped road, not yet replaced   → Undo it all (its tasks are idempotent)
 *   bench road, still on the order   → Unlock (release was never done, and
 *                                       release spends an override code)
 *   on_hold                          → Original condition (good: reset pending)
 *   in_stock, good, reset recorded   → Pick the replacement
 *   in_stock, anything else          → Original condition (reset / damage not
 *                                       recorded yet)
 *   rma / retired / lost             → Pick the replacement
 */
export function resumeStepFor(i: ResumeInput): ReplaceStep {
  const placed = (): ReplaceStep => {
    if (i.pending.lane === 'pos' && !i.osInstalled) return 'install';
    if (!i.attachComplete) return 'attach';
    if (i.pending.lane === 'payment') return i.readerInPool ? 'tender' : 'reader';
    return 'attach';
  };
  if (i.replacementOnOrder) return placed();
  if (i.pending.postDispatch) return i.old.replacedByUnitId ? placed() : 'detach';
  if (i.old.orderId && i.old.orderId === i.orderId) return 'unlock';
  switch (i.old.state) {
    case 'on_hold': return 'restore';
    case 'in_stock':
      return i.pending.outcome === 'good' && i.restoreRecorded ? 'pick' : 'restore';
    case 'rma':
    case 'retired':
    case 'lost':
      return 'pick';
    default:
      return 'restore';
  }
}
